import { Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { CheckIcon } from '@shared/custom-icons';
import { CentralAssortmentItem } from '@retail/my-assortment/types';
import { ExternalItemSource } from '@retail/products/components';
import { getLLDate } from '@retail/utils';

interface Props {
  inAssortment?: boolean;
  priorityValidFrom: Date;
  priorityValidTo?: Date;
  replacementPriority?: CentralAssortmentItem;
  externalItemSource?: ExternalItemSource;
}

export const AssortmentItemStatus = ({
  inAssortment,
  priorityValidFrom,
  priorityValidTo,
  replacementPriority,
  externalItemSource,
}: Props) => {
  const theme = useTheme();
  const t = useMyAssortmentTFunction();

  if (replacementPriority) {
    const replacementInfo = [
      {
        label: t('myAssortment.status.replacementItem.mgItemNumber'),
        value: replacementPriority.mgItemNumber,
      },
      externalItemSource === 'NOBB'
        ? {
            label: t('myAssortment.status.replacementItem.nobbNumber'),
            value: replacementPriority.nobbNumber,
          }
        : {
            label: t('myAssortment.status.replacementItem.finfoNumber'),
            value: replacementPriority.finfoNumber,
          },
      {
        label: t('myAssortment.status.replacementItem.supplierItemId'),
        value: replacementPriority.supplierItemId,
      },
      {
        label: t('myAssortment.status.replacementItem.supplierName'),
        value: replacementPriority.supplier.name,
      },
      {
        label: t('myAssortment.status.replacementItem.supplierId'),
        value: replacementPriority.supplier.id,
      },
    ].filter(({ value }) => !!value);

    return (
      <Tooltip
        title={
          <Stack gap={0.5}>
            <Typography variant="body1">
              {t('myAssortment.status.replacedFrom', {
                date: getLLDate(replacementPriority.priority.validFrom),
              })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {replacementPriority.primaryText}
            </Typography>
            {replacementPriority.secondaryText && (
              <Typography variant="body2" fontWeight={600}>
                {replacementPriority.secondaryText}
              </Typography>
            )}
            {replacementInfo.map(({ label, value }) => (
              <Typography key={label} variant="body2">
                {label}:{' '}
                <Typography component="span" variant="body2" fontWeight={600}>
                  {value}
                </Typography>
              </Typography>
            ))}
          </Stack>
        }
      >
        <Stack alignItems={'center'} gap={0.5}>
          <SwapHoriz
            fontSize="small"
            sx={{ color: theme.vars.palette.info.main }}
            titleAccess={t('myAssortment.status.replaced')}
          />
        </Stack>
      </Tooltip>
    );
  }

  if (priorityValidTo) {
    return (
      <Tooltip
        title={
          <Stack alignItems="center">
            {t('myAssortment.columns.removedFrom')} {getLLDate(priorityValidTo)}
          </Stack>
        }
      >
        <Stack alignItems={'center'} gap={0.5}>
          <CheckIcon color={theme.vars.palette.warning.main} />
        </Stack>
      </Tooltip>
    );
  }

  if (priorityValidFrom > new Date()) {
    return (
      <Tooltip title={t('myAssortment.columns.futurePrice')}>
        <Stack alignItems={'center'} gap={0.5}>
          <CheckIcon color={theme.vars.palette.secondary.main} />
        </Stack>
      </Tooltip>
    );
  }

  if (inAssortment) {
    return (
      <Tooltip title={t('myAssortment.status.inAssortment')}>
        <Stack alignItems={'center'} gap={0.5}>
          <CheckIcon color={theme.vars.palette.success.main} />
        </Stack>
      </Tooltip>
    );
  }
  return null;
};
